import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { AUTO_RETRY_DELAYS_MS, aiFailure, uploadForIdentification } from '../../utils/aiIdentify';
import { acceptBlockedReason } from '../../utils/aiThresholds';
import { rareFindOf } from '../../utils/handoffs';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { SafetyBanner, safetyOf } from '../TaxonSafety';
import RareFindPrompt from '../handoffs/RareFindPrompt';
import DnaReferenceCheck from './DnaReferenceCheck';
import ReferenceCompare from './ReferenceCompare';
import FieldTraitChecks from './FieldTraitChecks';
import { fieldTraitAsks } from '../../utils/ai/fieldTraits';
import Button from '../ui/Button';
import { usePrefersReducedMotion } from '../organic/motion';

/**
 * The live AI panel over the camera (docs/FIELD_AI_VISION_SPEC.md sections
 * 13-14, phase A3): a mushroom circled on the picture is asked about at once.
 * It floats on the side of the screen away from the loop, so the circle stays
 * where it was drawn, over the frozen frame, while the answer comes.
 *
 *   starting     "AI identification on... starting" with a spinner, from the
 *                moment the loop closes: the circled area going up
 *                (uploadForIdentification), then POST /ai/identify for the
 *                top 5. A cold model (503 ai_busy) is asked again by itself,
 *                as AiIdentifyPanel does, without uploading again.
 *   done         the top 5, each with its name (the Hebrew one where the
 *                dictionary has it, and the Latin), its match in %, and a
 *                photograph of that taxon from the bank
 *                (GET /photos/reference?limit=1, its small copy), else a
 *                neutral placeholder: never an outside picture. The server's
 *                safety banner first; a flagged row says so. Nothing is
 *                accepted here: the name is chosen in the capture summary.
 *                Lab staff may press for the circled area beside the lab's
 *                vetted photographs (ReferenceCompare, L12). Under the matches,
 *                a request to go and look at the base or the stem
 *                (FieldTraitChecks, L16): the answers stay beside the result
 *                and never reach it.
 *   failed       the honest reason (utils/aiIdentify.js aiFailure), with Try
 *                again where it can help. Offline, and dev's prod-only (the
 *                camera's warm-up already said so), ask nothing at all.
 *
 * A High-certainty answer naming a rare species (utils/handoffs.js
 * rareFindOf, the server's scales) shows the B6 rare-find prompt right here:
 * the collector is still standing by the find. The host keeps the request
 * with the specimen being photographed (`onHandoff`), as the summary does.
 *
 * Not modal: the circle and the picture stay in view, and Escape or Close
 * goes back to the live camera. `onKeep` keeps the frozen frame as a photo of
 * this specimen; `kept` says it was.
 */
export default function LiveIdentifyPanel({
  crop, user, side = 'bottom', offset = 0, aiEnv = null, waiting = false, upload = uploadForIdentification,
  requestConfig, onResult, onError, onClose, onKeep, kept = false,
  handoff = null, location = null, mayCollect = false, onHandoff, onCollect, testId = 'live-ai',
  traitAnswers = null, onTraitAnswer, traitFraming = false, mayRecordTraits = false, traitsHeld = false,
}) {
  const { t, i18n } = useTranslation('ai');
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState('uploading');
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [rare, setRare] = useState(null);
  const keyRef = useRef(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const headingRef = useRef(null);
  const post = (url, body) => (requestConfig ? api.post(url, body, requestConfig) : api.post(url, body));

  const ask = useCallback(async (tries = 0) => {
    clearTimeout(timer.current);
    setFailure(null);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setFailure({ key: 'offline' });
      setPhase('failed');
      return;
    }
    if (aiEnv?.prodOnly || aiEnv?.configured === false) {
      setFailure({ key: aiEnv.prodOnly ? 'prodOnly' : 'notConfigured' });
      setPhase('failed');
      return;
    }
    try {
      if (!keyRef.current) {
        setPhase('uploading');
        const file = new File([crop], 'encircle.jpg', { type: 'image/jpeg' });
        [keyRef.current] = await upload([file], requestConfig);
        if (!alive.current) return;
      }
      setPhase('identifying');
      const { data } = await post('/ai/identify', { s3_key: keyRef.current, top_k: 5, metadata: {} });
      if (!alive.current) return;
      setResult(data);
      setPhase('done');
      onResult?.(data);
      rareFindOf(data, user, { config: requestConfig }).then((found) => { if (alive.current) setRare(found); });
    } catch (e) {
      if (!alive.current) return;
      const why = e?.response ? aiFailure(e) : { key: 'failed', status: null, code: null, busy: false, detail: '' };
      if (why.busy && tries < AUTO_RETRY_DELAYS_MS.length) {
        setAttempt(tries + 1);
        setPhase('warming');
        timer.current = setTimeout(() => ask(tries + 1), AUTO_RETRY_DELAYS_MS[tries]);
        return;
      }
      onError?.({ status: why.status, code: why.code, key: why.key });
      setFailure(why);
      setPhase('failed');
    }
  // `post` is rebuilt each render from `requestConfig`, which is in the list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crop, upload, requestConfig, aiEnv, user, onResult, onError]);

  // Asked once the camera's warm-up has answered (it says when nothing would
  // come: dev's prod-only), never before; once.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || waiting) return;
    started.current = true;
    ask();
  }, [waiting, ask]);

  useEffect(() => {
    alive.current = true;
    headingRef.current?.focus?.({ preventScroll: true });
    return () => { alive.current = false; clearTimeout(timer.current); };
  }, []);

  // Escape goes back to the camera, unless a dialog opened from here (the
  // handoff form) took it first (components/ui/useModal.js).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const loading = phase === 'uploading' || phase === 'identifying' || phase === 'warming';
  const predictions = (result?.predictions || []).slice(0, 5);
  const safety = result ? safetyOf(result) : null;
  const dangerous = result ? acceptBlockedReason(result) === 'dangerous' : false;
  const hebrew = String(i18n?.language || '').startsWith('he');
  const retryable = failure && ['failed', 'busy', 'offline'].includes(failure.key);
  const place = side === 'top'
    ? { top: 'calc(env(safe-area-inset-top) + 64px)' }
    : { bottom: `${offset + 8}px` };

  return (
    <section role="dialog" aria-modal="false" aria-labelledby={`${testId}-title`} aria-busy={loading || undefined}
      data-testid={testId} data-phase={phase} data-side={side} style={{ ...st.panel, ...place }}>
      <style>{SPIN_CSS}</style>
      <div style={st.head}>
        <h2 id={`${testId}-title`} ref={headingRef} tabIndex={-1} style={st.title}>{t('live.title')}</h2>
        <button type="button" onClick={onClose} style={st.close} aria-label={t('live.close')} title={t('live.close')}
          data-testid={`${testId}-x`}>
          <span aria-hidden="true">×</span>
        </button>
      </div>

      {loading && (
        <div role="status" style={st.loading} data-testid={`${testId}-loading`}>
          <span aria-hidden="true" style={{ ...st.spinner, animation: reduced ? 'none' : 'live-ai-spin 0.9s linear infinite' }} />
          <div>
            <strong style={st.starting} data-testid={`${testId}-starting`}>{t('live.starting')}</strong>
            <span style={st.step} data-testid={`${testId}-step`}>
              {phase === 'warming'
                ? t('warming', { attempt, total: AUTO_RETRY_DELAYS_MS.length })
                : t(`live.${phase}`)}
            </span>
          </div>
        </div>
      )}

      {phase === 'failed' && failure && (
        <div style={st.failed}>
          <p role="alert" style={st.alert} data-testid={`${testId}-error`} data-key={failure.key}>
            {t(failure.key === 'offline' ? 'live.offline' : failure.key)}
          </p>
          {failure.detail && <p dir="ltr" style={st.detail}>{failure.detail}</p>}
          {retryable && (
            <Button onClick={() => ask()} title={t('tip.retry')} data-testid={`${testId}-again`}>
              {t('retry')}
            </Button>
          )}
        </div>
      )}

      {phase === 'done' && (
        <>
          {safety && <SafetyBanner safety={safety} t={t} />}
          {predictions.length === 0 ? (
            <p style={st.muted} data-testid={`${testId}-empty`}>{t('empty')}</p>
          ) : (
            <>
              <h3 style={st.subtitle}>{t('live.best')}</h3>
              <ol style={st.list} data-testid={`${testId}-list`}>
                {predictions.map((p, i) => (
                  <Candidate key={`${p.taxon_name}-${i}`} p={p} i={i} t={t} hebrew={hebrew}
                    requestConfig={requestConfig} testId={testId} />
                ))}
              </ol>
            </>
          )}
          {dangerous && <p role="note" style={st.caution} data-testid={`${testId}-dangerous`}>{t('live.dangerous')}</p>}
          {onTraitAnswer && (
            <FieldTraitChecks asks={fieldTraitAsks(result)} answers={traitAnswers} onAnswer={onTraitAnswer} warned={dangerous}
              framing={traitFraming} mayRecord={mayRecordTraits} held={traitsHeld} testId={`${testId}-traits`} />
          )}
          <p style={st.disclaimer}>{t('disclaimer.notAnIdentification')}</p>
          <ReferenceCompare s3Key={keyRef.current} user={user} requestConfig={requestConfig} testId={`${testId}-references`} />
          <DnaReferenceCheck aiId={result.ai_id} user={user} requestConfig={requestConfig} testId={`${testId}-dna`} />
        </>
      )}

      {(rare || handoff) && (
        <RareFindPrompt find={rare} request={handoff} location={location} mayCollect={mayCollect && !!rare}
          onCollect={onCollect} onRequest={onHandoff} onRemove={() => onHandoff?.(null, null)}
          testId={`${testId}-rare`} />
      )}

      <div style={st.actions}>
        <Button variant="primary" onClick={onKeep} disabled={kept} data-testid={`${testId}-keep`}
          title={t('live.keepTip')}>
          {kept ? t('live.kept') : t('live.keep')}
        </Button>
        <Button onClick={onClose} data-testid={`${testId}-close`}>{t('live.closeShort')}</Button>
      </div>
    </section>
  );
}

// One request per taxon name, shared by every panel this visit.
const thumbs = new Map();

/** One candidate: a photograph of its taxon from the bank, its names, its match. */
function Candidate({ p, i, t, hebrew, requestConfig, testId }) {
  const name = p.taxon_name || p.taxon || '';
  const pct = Math.round((Number(p.confidence) || 0) * 100);
  const flagged = !!(p.toxicity_flag || p.safety?.flags?.length || p.safety?.lookalike_of?.length
    || p.safety?.same_genus?.length);
  const rank = String(p.candidate_rank || '').toLowerCase();
  const local = p.hebrew_name && hebrew ? p.hebrew_name : null;
  return (
    <li style={st.row} data-testid={`${testId}-row-${i}`} data-taxon={name} data-pct={pct}>
      <Thumb taxon={name} requestConfig={requestConfig} t={t} testId={`${testId}-thumb-${i}`} />
      <div style={st.names}>
        {local && <span style={st.local}>{local}</span>}
        <bdi dir="ltr" style={local ? st.latinSmall : st.latin}>{name}</bdi>
        {['genus', 'family'].includes(rank) && <span style={st.rank}>{t(`live.rank.${rank}`)}</span>}
        {flagged && (
          <span style={st.flag} data-testid={`${testId}-flag-${i}`}>
            <span aria-hidden="true">⚠ </span>{t('live.flagged')}
          </span>
        )}
      </div>
      <div style={st.score}>
        <strong data-testid={`${testId}-pct-${i}`}><bdi dir="ltr">{pct}%</bdi></strong>
        <span style={st.bar} aria-hidden="true"><span style={{ ...st.fill, width: `${Math.max(2, pct)}%` }} /></span>
      </div>
    </li>
  );
}

function Thumb({ taxon, requestConfig, t, testId }) {
  const [photo, setPhoto] = useState(() => (thumbs.get(taxon)?.done ? thumbs.get(taxon).photo : undefined));
  useEffect(() => {
    if (!taxon || taxon.length < 2) { setPhoto(null); return undefined; }
    let live = true;
    if (!thumbs.has(taxon)) {
      const entry = { done: false, photo: null };
      entry.promise = Promise.resolve(api.get('/photos/reference', { params: { taxon, limit: 1 }, ...(requestConfig || {}) }))
        .then((res) => { entry.photo = res?.data?.photos?.[0] || null; })
        .catch(() => { entry.photo = null; })
        .finally(() => { entry.done = true; });
      thumbs.set(taxon, entry);
    }
    thumbs.get(taxon).promise.then(() => { if (live) setPhoto(thumbs.get(taxon).photo); });
    return () => { live = false; };
  }, [taxon, requestConfig]);
  const src = photo ? resolvePhotoUrl(photo.thumbnail_url || photo.s3_url) : null;
  return src ? (
    <img src={src} alt={t('live.photoOf', { taxon })} loading="lazy" style={st.thumb} data-testid={testId} />
  ) : (
    <span style={{ ...st.thumb, ...st.placeholder }} role="img" aria-label={t('live.noPhoto')}
      title={photo === undefined ? undefined : t('live.noPhoto')} data-testid={testId} data-empty="true">
      <span aria-hidden="true">🍄</span>
    </span>
  );
}

/** For tests: forget the photographs already asked for. */
export function forgetThumbs() {
  thumbs.clear();
}

const SPIN_CSS = '@keyframes live-ai-spin{to{transform:rotate(360deg)}}';

const st = {
  panel: {
    position: 'absolute', zIndex: 3, insetInlineStart: 12, insetInlineEnd: 12, margin: '0 auto', maxWidth: 520,
    maxHeight: 'min(56dvh, 520px)', overflowY: 'auto', boxSizing: 'border-box', padding: '10px 12px 12px',
    borderRadius: 14, background: 'var(--c-surface)', color: 'var(--c-text-body-fg)',
    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.45)', border: '1px solid var(--c-border-strong)',
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  title: { margin: 0, fontSize: 16 },
  close: { minWidth: 44, minHeight: 44, border: 'none', background: 'none', fontSize: 24, lineHeight: 1,
    cursor: 'pointer', color: 'inherit' },
  loading: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' },
  spinner: { flex: '0 0 auto', width: 28, height: 28, borderRadius: '50%', border: '3px solid var(--c-border-strong)',
    borderTopColor: 'var(--c-primary)', display: 'inline-block' },
  starting: { display: 'block', fontSize: 15 },
  step: { display: 'block', fontSize: 13, color: 'var(--c-text-muted-fg)', marginTop: 2 },
  failed: { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', padding: '6px 0' },
  alert: { margin: 0, fontSize: 14, color: 'var(--c-danger-fg)' },
  detail: { margin: 0, fontSize: 12, color: 'var(--c-text-muted-fg)' },
  subtitle: { margin: '6px 0 4px', fontSize: 13, color: 'var(--c-text-muted-fg)', fontWeight: 600 },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  row: { display: 'grid', gridTemplateColumns: '48px 1fr auto', alignItems: 'center', gap: 10,
    padding: 6, borderRadius: 10, background: 'var(--c-bg-subtle)' },
  thumb: { width: 48, height: 48, borderRadius: 8, objectFit: 'cover', display: 'block' },
  placeholder: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
    background: 'var(--c-border, rgba(127,127,127,0.2))', opacity: 0.8 },
  names: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  local: { fontWeight: 700, fontSize: 15 },
  latin: { fontStyle: 'italic', fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis' },
  latinSmall: { fontStyle: 'italic', fontSize: 13, color: 'var(--c-text-muted-fg)', overflow: 'hidden',
    textOverflow: 'ellipsis' },
  rank: { fontSize: 12, color: 'var(--c-text-muted-fg)' },
  flag: { fontSize: 12, fontWeight: 700, color: 'var(--c-danger-fg)' },
  score: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 56, fontSize: 15 },
  bar: { display: 'block', width: 56, height: 6, borderRadius: 3, background: 'var(--c-border, rgba(127,127,127,0.25))',
    overflow: 'hidden' },
  fill: { display: 'block', height: '100%', background: 'var(--c-primary)' },
  muted: { margin: '6px 0', fontSize: 14, color: 'var(--c-text-muted-fg)' },
  caution: { margin: '8px 0 0', fontSize: 13, fontWeight: 600, color: 'var(--c-danger-fg)' },
  disclaimer: { margin: '8px 0 0', fontSize: 12, color: 'var(--c-text-muted-fg)' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
};
